import { atom, useSetAtom } from 'jotai';
import { atomWithReset, atomWithStorage } from 'jotai/utils';
import callApi from 'lib/callApi';
import { companyIdAtom } from 'views/state';
import { DEFAULT_PAGE_SIZE } from 'views/types';

import { epRefreshAtom } from '../state';

export const forceRerenderUIAtom = atom<string>('');

export const orderByAtom = atomWithReset<string>('vuln');
export const sortOrderAtom = atomWithReset<'desc' | 'asc'>('desc');

export const orderingAtom = atom(async (get) => {
  const orderBy = get(orderByAtom);
  const order = get(sortOrderAtom);
  return `${order === 'desc' ? '-' : ''}${orderBy}`;
});

export const checkedListAtom = atomWithReset<number[]>([]);
export const isCheckAllAtom = atomWithReset(false);

export const DEFAULT_ACTIVE_EP_LISTING_COLUMNS = [
  'ip_with_type',
  'alias',
  'asset_name',
  'vuln',
  'created_at',
  'scan__launch_datetime',
  'actions',
];
export const activeEPColumnKeysAtom = atomWithStorage(
  'ep_list_active_column_keys',
  DEFAULT_ACTIVE_EP_LISTING_COLUMNS,
);

export const DEFAULT_AGED_OUT_EP_LISTING_COLUMNS = [
  'ip_with_type',
  'alias',
  'asset_name',
  'operating_system',
  'deleted_endpoint__open_vulnerability_count',
  'deleted_endpoint__closed_vulnerability_count',
  'deleted_endpoint__deleted_on',
  'deleted_endpoint__deleted_by',
  'actions',
];
export const agedOutEPColumnKeysAtom = atomWithStorage(
  'ep_list_aged_out_column_keys',
  DEFAULT_AGED_OUT_EP_LISTING_COLUMNS,
);

export const paginationConfigAtom = atomWithStorage(
  'ep_dashboard_pagination_config',
  {
    page_size: DEFAULT_PAGE_SIZE,
    page: 1,
  },
);

// component height
export const componentHeightAtom = atom(0);

// Add a new atom to store vulnerability counts for checked hosts
export const checkedHostsVulnMapAtom = atom<Record<number, number>>({});

export const vulnCountForCheckedHostsAtom = atom(async (get) => {
  const checkedHostIds = get(checkedListAtom);
  const vulnMap = get(checkedHostsVulnMapAtom);
  let total = 0;

  if (checkedHostIds?.length > 0) {
    const allHosts = get(epListingDataAtom);

    // Update vulnMap with any new hosts from current page
    const currentPageHosts = allHosts?.items?.filter((host) =>
      checkedHostIds.includes(host.id),
    );

    // Calculate total using both stored and current page values
    checkedHostIds.forEach((hostId) => {
      // Use stored value if available, otherwise 0
      total += vulnMap[hostId] || 0;
    });

    // Update totals with any new values from current page
    currentPageHosts?.forEach((host) => {
      if (host.vuln !== vulnMap[host.id]) {
        total = total - (vulnMap[host.id] || 0) + (host.vuln || 0);
      }
    });
  }
  return total;
});

interface Insights {
  status_breakdown: {
    active: number;
    aging: number;
    deleted: number;
  };
  severity_breakdown: {
    urgent: number;
    critical: number;
    serious: number;
    medium: number;
    minimal: number;
  };
  internal_count: number;
  external_count: number;
  aws_count: number;
  total_vulnerabilities: number;
  unique_os_count: number;
}

export const epListingDataAtom = atom({
  items: [] as any[],
  count: 0,
  loading: false,
  insights: {} as Insights,
});

export const forceRefreshStatusAtom = atom<string>('');
export const endpointsStatusDataAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const forceRefresh = get(forceRefreshStatusAtom);
  const endpointData = get(epListingDataAtom);
  try {
    // only load on force refresh
    if (companyId && companyId !== -1 && endpointData?.items && forceRefresh) {
      const params = {
        company_id: companyId,
        endpoint_ids: endpointData?.items.map((i) => i.id).join(','),
      };
      const responseData = await callApi('endpoints/status', 'GET', params);
      return responseData?.data;
    }
  } catch (error) {
    console.error(error);
  }
  return undefined;
});

export const parsedFilterValuesAtom = atom<Record<string, any>>((get) => {
  const filterValues = get(filterValuesAtom);
  const updatedFilterValues = {
    ...filterValues,
    ...DateFilterKeys.reduce((acc, key) => {
      if (filterValues[key]) {
        acc[key] = filterValues[key].map((dateRange) => ({
          from: dateRange.from ? new Date(dateRange.from) : null,
          to: dateRange.to ? new Date(dateRange.to) : null,
        }));
      }
      return acc;
    }, {}),
  };
  return updatedFilterValues;
});

// New Filters Atoms
const DateFilterKeys = ['created_at', 'updated_at', 'last_vm_scanned_date'];
export const filterValuesAtom = atomWithReset<Record<string, any>>({
  filter: ['active'],
});
export const isQuerystringFiltersInitializedAtom =
  atomWithReset<boolean>(false);
export const epDataQueryParamsAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const paginationConfig = get(paginationConfigAtom);
  const pageSize = paginationConfig.page_size;
  const currentPage = paginationConfig.page;

  const orderBy = get(orderByAtom);
  const order = get(sortOrderAtom);

  // Add id to orderBy if not present, then apply sort order
  const orderByWithId = orderBy
    .split(',')
    .some((field) => field.trim() === 'id' || field.trim() === '-id')
    ? orderBy
    : `${orderBy},id`;
  const ordering = orderByWithId
    .split(',')
    .map(
      (field) =>
        `${order === 'desc' && !field.startsWith('-') ? '-' : ''}${field}`,
    )
    .join(',');
  const filterValues = get(filterValuesAtom);
  const _epDataRefresh = get(epRefreshAtom);

  if (companyId && companyId !== -1) {
    const params = {
      company_id: companyId.toString(),
      offset: pageSize * (currentPage - 1),
      limit: pageSize,
      ordering: ordering,
    };

    Object.entries(filterValues).forEach(([key, value]) => {
      if (value && value.length > 0) {
        if (DateFilterKeys.includes(key)) {
          const fromDate = value[0]?.from;
          const toDate = value[0]?.to;
          if (fromDate instanceof Date) {
            params[`${key}_after`] = fromDate.toISOString();
          } else {
            // eslint-disable-next-line no-console
            console.error({
              msg: 'fromDate is not a valid Date object:',
              fromDate,
            });
          }
          if (toDate) params[`${key}_before`] = toDate?.toISOString();
        } else if (key === 'scanner_type') {
          if (['Server', 'Workstations'].includes(value[0])) {
            params['scanner_type'] = value[0];
          } else {
            params['scanner_type'] = value[0];
          }
        } else {
          params[key] = value.join(',');
        }
      }
    });

    return params;
  }
});

// Reset function for filter changes
export const useOnFilterChangeResetAtoms = () => {
  const setCheckedList = useSetAtom(checkedListAtom);
  const setPaginationConfig = useSetAtom(paginationConfigAtom);

  return () => {
    setCheckedList([]);
    setPaginationConfig((prev) => ({ ...prev, page: 1 }));
  };
};
